import { useGaiaStore } from '@/stores/gaiaStore';
import { useVaultStore } from '@/stores/vaultStore';
import { computed, ComputedRef, Ref, ref } from 'vue';
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';

export interface ParsedKeys {
  fileKeys: string[];
  dirKeys: string[];
  gaiaKeys: string[];
}

export interface SelectedUids {
  fileUids: string[];
  dirUids: string[];
  gaiaUids: string[];
}

export interface FileSelectionReturn {
  selectedFileKeys: Ref<string[]>;
  parsedKeys: ComputedRef<ParsedKeys>;
  fileVaultKeys: ComputedRef<string[]>;
  gaiaKeys: ComputedRef<string[]>;
  getAllSelectedUids: ComputedRef<SelectedUids>;
  importGaiaFiles: () => Promise<string[]>;
  updateSelection: (newKeys: string[]) => void;
  addToSelection: (keys: string[]) => void;
  removeFromSelection: (keys: string[]) => void;
  clearSelection: () => void;
  updateVaultSelection: (vaultKeys: string[]) => void;
  updateGaiaSelection: (gaiaKeys: string[]) => void;
}

export function useFileSelection(): FileSelectionReturn {
  const vaultStore = useVaultStore();
  const gaiaStore = useGaiaStore();
  const message = useGaiaToast();

  // Internal state for selected keys
  const selectedFileKeys = ref<string[]>([]);

  // Parse selected keys into different types
  const parsedKeys = computed(() => {
    const fileKeys = [] as string[];
    const dirKeys = [] as string[];
    const gaiaKeys = [] as string[];

    for (const key of selectedFileKeys.value) {
      if (key.startsWith('file-')) fileKeys.push(key);
      else if (key.startsWith('dir-')) dirKeys.push(key);
      else if (key.startsWith('gaia-')) gaiaKeys.push(key);
    }

    return { fileKeys, dirKeys, gaiaKeys };
  });

  // Convenience computed properties for different key types
  const fileVaultKeys = computed(() => [...parsedKeys.value.fileKeys, ...parsedKeys.value.dirKeys]);
  const gaiaKeys = computed(() => parsedKeys.value.gaiaKeys);

  // Get all selected UIDs (without prefixes)
  const getAllSelectedUids = computed(() => {
    const { fileKeys, dirKeys, gaiaKeys } = parsedKeys.value;

    return {
      fileUids: fileKeys.map((key: string) => key.replace('file-', '')),
      dirUids: dirKeys.map((key: string) => key.replace('dir-', '')),
      gaiaUids: gaiaKeys.map((key: string) => key.replace('gaia-', '')),
    };
  });

  // Import GAIA files to vault
  const importGaiaFiles = async (): Promise<string[]> => {
    const { gaiaKeys } = parsedKeys.value;
    const importedFileUids: string[] = [];

    if (gaiaKeys.length === 0) {
      return importedFileUids;
    }

    message.info(`Importing ${gaiaKeys.length} GAIA file(s) to vault...`);

    for (let i = 0; i < gaiaKeys.length; i++) {
      const gaiaKey = gaiaKeys[i];
      const gaiaFileId = gaiaKey.replace('gaia-', '');
      const gaiaFile = gaiaStore.getGaiaFileById(gaiaFileId);
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      const fileName = gaiaFile?.title || `File ${gaiaFileId}`;

      try {
        message.info(`Importing ${fileName} (${i + 1}/${gaiaKeys.length})...`);
        const importedFile = await gaiaStore.importGaiaFileToVault(gaiaFileId);
        importedFileUids.push(importedFile.uid);
      } catch (error) {
        logger.error(`Failed to import GAIA file ${gaiaFileId}:`, error);
        const errorMsg = extractErrorDetail(error);
        message.error(`Failed to import ${fileName}: ${errorMsg}`);
        throw error;
      }
    }

    message.success(`Successfully imported ${importedFileUids.length} GAIA file(s) to vault`);
    await vaultStore.getVaultDirectory();

    return importedFileUids;
  };

  // Helper function to extract meaningful error messages
  const extractErrorDetail = (err: unknown): string => {
    if (!err) return 'Failed to import file. Please try again.';

    if (typeof err === 'string') {
      const detailMatch = err.match(/detail":\s*"([^"]*)"/);
      if (detailMatch && detailMatch[1]) {
        return detailMatch[1];
      }
      return err;
    }

    if (err instanceof Error) {
      const errorMsg = err.message;
      const bodyMatch = errorMsg.match(/body:\s*(\{.*\})/);
      if (bodyMatch) {
        try {
          const body = JSON.parse(bodyMatch[1]);
          if (body.detail) return body.detail;
        } catch {
          // If parsing fails, continue with other extraction methods
        }
      }

      const detailMatch = errorMsg.match(/detail":\s*"([^"]*)"/);
      if (detailMatch && detailMatch[1]) {
        return detailMatch[1];
      }

      return errorMsg;
    }

    if (typeof err === 'object') {
      if ('detail' in err) return (err as { detail: string }).detail;
      if ('message' in err) return (err as { message: string }).message;
      if ('error' in err) return (err as { error: string }).error;
    }

    return 'Failed to import file. Please try again.';
  };

  // Update selection with new keys
  const updateSelection = (newKeys: string[]): void => {
    selectedFileKeys.value = newKeys;
  };

  // Add keys to current selection
  const addToSelection = (keys: string[]): void => {
    const currentSet = new Set(selectedFileKeys.value);
    for (const key of keys) currentSet.add(key);
    selectedFileKeys.value = Array.from(currentSet);
  };

  // Remove keys from current selection
  const removeFromSelection = (keys: string[]): void => {
    const currentSet = new Set(selectedFileKeys.value);
    for (const key of keys) currentSet.delete(key);
    selectedFileKeys.value = Array.from(currentSet);
  };

  // Clear all selections
  const clearSelection = (): void => {
    selectedFileKeys.value = [];
  };

  // Helper functions for updating specific key types
  const updateVaultSelection = (vaultKeys: string[]): void => {
    selectedFileKeys.value = [...vaultKeys, ...gaiaKeys.value];
  };

  const updateGaiaSelection = (gaiaKeys: string[]): void => {
    selectedFileKeys.value = [...fileVaultKeys.value, ...gaiaKeys];
  };

  return {
    // State
    selectedFileKeys,

    // Computed
    parsedKeys,
    fileVaultKeys,
    gaiaKeys,
    getAllSelectedUids,

    // Actions
    importGaiaFiles,
    updateSelection,
    addToSelection,
    removeFromSelection,
    clearSelection,
    updateVaultSelection,
    updateGaiaSelection,
  };
}
